import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BarChart3, RefreshCw } from "lucide-react";

export default function POSSalesReport() {
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [data, setData] = useState(null);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, b] = await Promise.all([
        api.get("/pos/reports/sales", { params: { date_from: dateFrom, date_to: dateTo } }),
        api.get("/pos/bills", { params: { date_from: dateFrom, date_to: dateTo, page_size: 200 } }),
      ]);
      setData(r.data); setBills(b.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-4 space-y-4 max-w-6xl mx-auto">
      <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-red-600" />
        <h2 className="text-lg font-semibold">Sales Report</h2>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40 ml-4" data-testid="rep-from" />
        <span className="text-xs text-slate-400">to</span>
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-40" data-testid="rep-to" />
        <Button onClick={load} disabled={loading} className="bg-red-600 hover:bg-red-700 ml-auto"><RefreshCw className="h-4 w-4 mr-1" />{loading ? "Loading…" : "Refresh"}</Button>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <Card k="Bills" v={data?.summary?.bills || 0} />
        <Card k="Gross" v={`₹${(data?.summary?.gross || 0).toFixed(2)}`} />
        <Card k="Tax" v={`₹${(data?.summary?.tax || 0).toFixed(2)}`} />
        <Card k="Discount" v={`₹${(data?.summary?.discount || 0).toFixed(2)}`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Payment Modes</p>
          {data?.by_mode ? Object.entries(data.by_mode).map(([k, v]) => <div key={k} className="flex items-baseline py-1"><span className="text-sm capitalize flex-1">{k}</span><span className="text-sm font-semibold tabular-nums">₹{Number(v).toFixed(2)}</span></div>) : <p className="text-slate-400 text-sm">No data</p>}
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Top Products</p>
          <div className="max-h-48 overflow-auto">
            {(data?.top_products || []).map((p) => <div key={p.product_id} className="flex items-baseline py-1 text-sm"><span className="flex-1 truncate">{p.product_name}</span><span className="text-slate-500 mx-2">×{p.qty}</span><span className="font-semibold tabular-nums">₹{p.amount.toFixed(2)}</span></div>)}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="p-3 border-b border-slate-100 text-sm font-semibold">Bills</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left text-[11px] uppercase tracking-widest text-slate-500"><th className="px-3 py-2">Bill #</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Customer</th><th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2">Status</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {bills.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">No bills.</td></tr>}
            {bills.map((b) => (
              <tr key={b.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs font-semibold text-red-700"><a href={`/pos/bills/${b.id}/print`} target="_blank" rel="noopener noreferrer" className="underline">{b.bill_no}</a></td>
                <td className="px-3 py-2 text-xs">{new Date(b.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">{b.customer_name}{b.customer_mobile ? ` · ${b.customer_mobile}` : ""}</td>
                <td className={`px-3 py-2 text-right font-semibold tabular-nums ${b.is_return ? "text-red-600" : ""}`}>₹{Number(b.totals?.grand_total || 0).toFixed(2)}</td>
                <td className="px-3 py-2 text-xs uppercase">{b.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ k, v }) {
  return <div className="bg-white rounded-lg border border-slate-200 p-3"><p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{k}</p><p className="text-2xl font-bold tabular-nums mt-1">{v}</p></div>;
}
